import {
  CreditTxTypeEnum,
  CreditTxWithMeta,
  PurchaseStatusEnum,
} from "@garther/interfaces";
import { Button } from "@garther/ui/components";
import {
  Coins,
  HandCoins,
  ShoppingCart,
  Star,
  TicketCheck,
} from "lucide-react";
import { useMemo } from "react";

import { useI18n } from "@garther/ui/libs";

// Transaction component
interface CreditTxItemProps {
  transaction: CreditTxWithMeta;
  className?: string;
  onPaymentClick: (transaction: CreditTxWithMeta) => void;
}

const TX_CFGS: Record<
  CreditTxTypeEnum,
  { icon: any; iconClassName: string; text: string }
> = {
  PAYMENT: {
    icon: TicketCheck,
    iconClassName: "bg-sky-400 text-text-inverse",
    text: "credits.transactionStatuses.pending",
  },
  PURCHASE: {
    icon: ShoppingCart,
    iconClassName: "bg-success-400 text-text-inverse",
    text: "credits.transactionStatuses.pending",
  },
  USAGE: {
    icon: TicketCheck,
    iconClassName: "bg-warning-400 text-text-inverse",
    text: "credits.transactionStatuses.pending",
  },
  REFERRAL: {
    icon: Star,
    iconClassName: "bg-success-400 text-text-inverse",
    text: "credits.transactionStatuses.pending",
  },
  TICKET_SOLD: {
    icon: HandCoins,
    iconClassName: "bg-success-400 text-text-inverse",
    text: "credits.transactionStatuses.pending",
  },
  BONUS: {
    icon: Coins,
    iconClassName: "bg-success-400 text-text-inverse",
    text: "credits.transactionStatuses.pending",
  },
};

export const CreditTxItem = ({
  transaction,
  className,
  onPaymentClick,
}: CreditTxItemProps) => {
  const { t, formatCredit, formatDate } = useI18n();

  const txCfg = useMemo(() => TX_CFGS[transaction.type], [transaction.type]);

  const amountSign =
    transaction.type === CreditTxTypeEnum.PAYMENT
      ? ""
      : transaction.type === CreditTxTypeEnum.PURCHASE ||
          transaction.type === CreditTxTypeEnum.BONUS ||
          transaction.type === CreditTxTypeEnum.REFERRAL ||
          transaction.type === CreditTxTypeEnum.TICKET_SOLD
        ? "+"
        : "-";

  // Helper function to get payment status text and determine if button should be shown
  const getPaymentStatusInfo = () => {
    // Only purchase transactions have payment status
    if (
      transaction.type !== CreditTxTypeEnum.PURCHASE &&
      transaction.type !== CreditTxTypeEnum.PAYMENT
    ) {
      return { text: "", showButton: false };
    }

    if (transaction.purchaseMeta?.status === PurchaseStatusEnum.SUCCESS) {
      return { text: t("credits.transactionStatuses.paid"), showButton: false };
    }
    if (transaction.purchaseMeta?.status === PurchaseStatusEnum.FAILED) {
      return {
        text: t("credits.transactionStatuses.cancelled"),
        showButton: false,
      };
    }
    if (transaction.purchaseMeta?.status === PurchaseStatusEnum.PENDING) {
      // Check if pending transaction has expired
      if (transaction.purchaseMeta?.expiresAt) {
        const now = new Date();
        const expiresAt = new Date(transaction.purchaseMeta?.expiresAt);
        if (now > expiresAt) {
          return {
            text: t("credits.transactionStatuses.expired"),
            showButton: false,
          };
        }
      }
      return {
        text: t("credits.transactionStatuses.pending"),
        showButton: true,
      };
    }
    // Default case - assume pending if no status
    return { text: t("credits.transactionStatuses.pending"), showButton: true };
  };

  const paymentStatusInfo = getPaymentStatusInfo();

  return (
    <div
      className={`flex flex-col items-center gap-2 w-full py-3 px-1 ${className}`}
    >
      <div className="flex items-center gap-2 w-full justify-between">
        <div className="flex items-center">
          <div className={`${txCfg.iconClassName} p-1.5 rounded-md`}>
            <txCfg.icon className="w-6 h-6" strokeWidth={2} />
          </div>
        </div>
        <div className="flex flex-1 items-center justify-between">
          <div className="flex-1 flex flex-col">
            <div className="flex items-center justify-start gap-2">
              <span className={`text-responsive-base font-normal`}>
                {amountSign}
                {formatCredit(transaction.amount)}
              </span>
              {transaction.type === CreditTxTypeEnum.PURCHASE ||
                (transaction.type === CreditTxTypeEnum.PAYMENT &&
                  paymentStatusInfo.text && (
                    <span
                      className={`text-responsive-sm font-medium text-text-secondary bg-background-primary rounded-md px-3 py-1`}
                    >
                      {paymentStatusInfo.text}
                    </span>
                  ))}
            </div>
            <p className="text-responsive-sm font-normal text-text-secondary">
              {transaction.desc}
            </p>
          </div>

          <div className="text-end">
            <p className="text-responsive-sm font-normal text-text-secondary">
              {formatDate(new Date(transaction.createdAt))}
            </p>
            {paymentStatusInfo.showButton && (
              <Button
                variant="outline"
                padding="px-4 py-1.5 mt-2"
                className="text-responsive-base"
                onClick={() => onPaymentClick(transaction)}
              >
                {t("credits.payNow")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
